import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { sendPasswordReset } from "../API/firebase";
import { Eyebrow, PillButton, SurfaceCard } from "../components/ui";
import { paths } from "./paths";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;

    setSending(true);
    await sendPasswordReset(email.trim());
    setSending(false);
    // Deliberately unconditional: confirming whether an address has an account
    // would leak account existence.
    setSent(true);
  };

  return (
    <Box
      sx={{
        width: `min(100%, 480px)`,
        mx: "auto",
        px: { mobile: 2.5, tablet: 3 },
        py: { mobile: 5, laptop: 8 },
      }}
    >
      <Eyebrow>Account</Eyebrow>
      <Typography variant="h2" sx={{ mb: 3 }}>
        Reset your password.
      </Typography>

      <SurfaceCard>
        {sent ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              If an account exists for {email.trim()}, a reset link is on its way.
              Check your inbox and spam folder.
            </Alert>
            <PillButton tone="amber" onClick={() => navigate(paths.signInUp)}>
              Back to sign in
            </PillButton>
          </>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              Enter the email address on your account and we will send a reset
              link.
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
              <PillButton
                tone="outline"
                type="button"
                onClick={() => navigate(paths.signInUp)}
              >
                Back
              </PillButton>
              <PillButton
                tone="amber"
                type="submit"
                disabled={sending || !email.trim()}
              >
                {sending ? "Sending…" : "Send reset link"}
              </PillButton>
            </Box>
          </Box>
        )}
      </SurfaceCard>
    </Box>
  );
}
